import {
  WORKFLOW_BUILDER_AUTHORING_API,
  WORKFLOW_INSTRUCTION_ASSET_API,
  WORKFLOW_SERVICE_QUEUE_API,
  WORKFLOW_AUTHORING_EDITOR_OPTIONS,
} from '@shared/workflows/authoring';
import { HugerteLanguageService } from './shared/services/hugerte-language.service';

import { GeneratedWorkflowBuilderAuthoringApi } from './settings/workflows/workflow-builder/workflow-builder-authoring-api';
import { GeneratedWorkflowInstructionAssetApi } from './settings/workflows/workflow-builder/workflow-instruction-asset-api';
import { GeneratedWorkflowServiceQueueApi } from './settings/workflows/workflow-builder/workflow-service-queue-api';
import { HUGERTE_SCRIPT_SRC } from '@hugerte/hugerte-angular';

import {
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  NgModule,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import {
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ApiModule, Configuration, ConfigurationParameters } from '../api/gen';
import { environment } from '../environments/environment';
import { interceptorProviders } from './interceptors';
import { MenuBarComponent } from './menu/menu-bar/menu-bar.component';
import { ProfileComponent } from './profile/profile.component';
import { SchedulingModule } from './scheduling/scheduling.module';

import { NgOptimizedImage } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatStepperModule } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { SetupPlatformOverviewComponent } from './setup/setup-platform-overview/setup-platform-overview.component';
import { SharedModule } from './shared/shared.module';
import { SignupInvitationLandingComponent } from './signup/signup-invitation-landing/signup-invitation-landing.component';

import { QuotaDisplayComponent } from './menu/quota-display/quota-display.component';
import { NotificationBarComponent } from './menu/notification-bar/notification-bar.component';
import { PersonalInformationComponent } from './profile/personal-information/personal-information.component';
import { PersonalInformationContactComponent } from './profile/personal-information-contact/personal-information-contact.component';
import { EditContactDialogComponent } from './profile/dialogs/edit-contact-dialog/edit-contact-dialog.component';
import { MatSelectModule } from '@angular/material/select';
import { PersonalInformationAuthenticationComponent } from './profile/personal-information-authentication/personal-information-authentication.component';
import { PersonalInformationDepartmentComponent } from './profile/personal-information-department/personal-information-department.component';
import { EditDepartmentDialogComponent } from './profile/dialogs/edit-department-dialog/edit-department-dialog.component';
import { LoginComponent } from './authentication/login/login.component';
import { AuthCallbackComponent } from './authentication/auth-callback/auth-callback.component';

import * as Sentry from '@sentry/angular';
import { HelperChatService } from './chat/helper-chat-service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { NotificationCenterComponent } from './menu/notification-center/notification-center.component';
import { NotificationItemComponent } from './menu/notification-center/notification-item/notification-item.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatBadge } from '@angular/material/badge';
import { PersonalInformationLegalEntityComponent } from './profile/personal-information-legal-entity/personal-information-legal-entity.component';
import { EditLegalEntityDialogComponent } from './profile/dialogs/edit-legal-entity-dialog/edit-legal-entity-dialog.component';
import { MatExpansionPanel } from '@angular/material/expansion';
import { SideNavComponent } from './menu/side-nav/side-nav.component';
import { BrandingColorService } from './services/api/branding-color.service';
import { HeadingComponent } from './shared/heading/heading.component';
import { JoinOpenAppointmentButtonComponent } from './menu/join-open-appointment-button/join-open-appointment-button.component';
import { DateRangeComponent } from './scheduling/date-range/date-range.component';
import { MenuBarTitleComponent } from './menu/menu-bar-title/menu-bar-title.component';
import { MenuBarEmployeeInfoComponent } from './menu/menu-bar-employee-info/menu-bar-employee-info.component';
import { RealmService } from './authentication/realm.service';
import { ProfileHeaderComponent } from './profile/profile-header/profile-header.component';
import { ProfileSettingsComponent } from './profile/profile-settings/profile-settings.component';
import { TraceService } from '@sentry/angular';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  CALL_CONNECTION_RECOVERY_CONFIG,
  createCallConnectionRecoveryConfig,
  TRACKED_ACTION_SERVICE,
  USER_LANGUAGE_SETTINGS_ADAPTER,
} from 'shared/core';
import { HelperLanguageSettingsAdapterService } from './services/data/helper-language-settings-adapter.service';
import { HELPER_CLIENT_APP_CONFIG } from './config/helper-client-app-config';
import { EmbeddedCompletedComponent } from './embedded/embedded-completed.component';
import { SalesforceLaunchComponent } from './integrations/salesforce/salesforce-launch.component';
import { SalesforceNotAuthorizedComponent } from './integrations/salesforce/salesforce-not-authorized.component';
import { FeatureTrackingService } from './services/api/feature-tracking.service';
import { provideLottieOptions } from 'ngx-lottie';
import { FeedbackMessageComponent } from './shared-standalone/feedback-message/feedback-message.component';

// load `lottie-web` on demand
export function playerFactory() {
  return import(
    /* webpackChunkName: 'lottie-web' */ 'lottie-web/build/player/lottie_svg'
  );
}

export function apiConfigFactory(): Configuration {
  const params: ConfigurationParameters = {
    basePath: environment.apiPath,
  };
  return new Configuration(params);
}

@NgModule({
  declarations: [
    AppComponent,
    MenuBarComponent,
    ProfileComponent,
    SignupInvitationLandingComponent,
    SetupPlatformOverviewComponent,
    QuotaDisplayComponent,
    NotificationBarComponent,
    PersonalInformationComponent,
    PersonalInformationContactComponent,
    PersonalInformationAuthenticationComponent,
    PersonalInformationDepartmentComponent,
    PersonalInformationLegalEntityComponent,
    EditContactDialogComponent,
    EditDepartmentDialogComponent,
    EditLegalEntityDialogComponent,
    LoginComponent,
    AuthCallbackComponent,
    NotificationCenterComponent,
    ProfileHeaderComponent,
    EmbeddedCompletedComponent,
    SalesforceLaunchComponent,
    SalesforceNotAuthorizedComponent,
  ],
  bootstrap: [AppComponent],
  exports: [MenuBarComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    NotificationItemComponent,
    BrowserModule,
    FeedbackMessageComponent,
    AppRoutingModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    FormsModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatSidenavModule,
    ApiModule.forRoot(apiConfigFactory),
    MatSidenavModule,
    MatDividerModule,
    MatListModule,
    MatMenuModule,
    MatCardModule,
    MatSnackBarModule,
    MatDialogModule,
    MatTableModule,
    SchedulingModule,
    MatInputModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    NgOptimizedImage,
    SharedModule,
    ProfileSettingsComponent,
    MatStepperModule,
    MatOptionModule,
    MatSelectModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.testing || environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    MatTooltip,
    MatBadge,
    MatExpansionPanel,
    SideNavComponent,
    HeadingComponent,
    JoinOpenAppointmentButtonComponent,
    DateRangeComponent,
    MenuBarTitleComponent,
    MenuBarEmployeeInfoComponent,
  ],
  providers: [
    { provide: HUGERTE_SCRIPT_SRC, useValue: 'assets/hugerte/hugerte.min.js' },
    {
      provide: WORKFLOW_AUTHORING_EDITOR_OPTIONS,
      useFactory: () =>
        inject(HugerteLanguageService).getLocalizedEditorOptions(),
    },
    {
      provide: WORKFLOW_BUILDER_AUTHORING_API,
      useExisting: GeneratedWorkflowBuilderAuthoringApi,
    },
    {
      provide: WORKFLOW_INSTRUCTION_ASSET_API,
      useExisting: GeneratedWorkflowInstructionAssetApi,
    },
    {
      provide: WORKFLOW_SERVICE_QUEUE_API,
      useExisting: GeneratedWorkflowServiceQueueApi,
    },
    {
      provide: CALL_CONNECTION_RECOVERY_CONFIG,
      useValue: createCallConnectionRecoveryConfig(
        environment.uiFeatureFlags.callConnectionRecoveryEnabled,
      ),
    },
    {
      provide: 'ChatService',
      useClass: HelperChatService,
    },
    {
      provide: 'ChatAttachmentService',
      useExisting: HelperChatService,
    },
    {
      provide: 'BrandingColorApi',
      useClass: BrandingColorService,
    },
    {
      provide: USER_LANGUAGE_SETTINGS_ADAPTER,
      useExisting: HelperLanguageSettingsAdapterService,
    },
    {
      provide: TRACKED_ACTION_SERVICE,
      useExisting: FeatureTrackingService,
    },
    {
      provide: HELPER_CLIENT_APP_CONFIG,
      useValue: environment,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        logErrors: true,
        showDialog: false,
      }),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    provideAppInitializer(() =>
      inject(RealmService).resolve(window.location.hostname),
    ),
    interceptorProviders,
    provideLottieOptions({ player: playerFactory }),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    // TODO: Remove deprecated usage as soon as angular material components are no longer requiring provideAnimations (Angular23?)
    provideAnimations(),
  ],
})
export class AppModule {}
